import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import VisibilityOutlinedIcon from 'components/material-symbol/icons/Visibility'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import StarBorderOutlinedIcon from 'components/material-symbol/icons/StarBorder'
import LockOutlinedIcon from 'components/material-symbol/icons/Lock'
import CheckBoxOutlineBlankIcon from 'components/material-symbol/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from 'components/material-symbol/icons/CheckBox'
import {
  SettingsChoiceRow,
  SettingsCheckboxRow,
  SettingsSection,
} from 'pages/pga-coach/settings/settings-components'
import FacilityInformationFields from './facility-information-fields'

const checkboxIcon = <CheckBoxOutlineBlankIcon />
const checkboxCheckedIcon = <CheckBoxIcon fill={1} />

const SettingsFacilityFormSections = ({
  facility,
  control,
  register,
  setFacility,
  errors,
  preventExternalFacilityEdit,
  facilityPersisted,
  clearSelectedFacility,
  setValue,
  hasCoachProfile,
  classes,
  settingsStyles,
  layoutStyles,
}) => {
  const isPrimary = facility
    ? facility?.coachProfilesFacilities[0]?.facilityRank === 1
    : false

  return (
    <>
      {hasCoachProfile && (
        <SettingsSection
          eyebrow="Coach Profile Visibility"
          classes={settingsStyles}
        >
          {isPrimary ? (
            <Box className={layoutStyles.infoBannerWrapper}>
              <Box className={layoutStyles.infoBanner}>
                <Box className={layoutStyles.infoBannerCopy}>
                  <InfoOutlinedIcon
                    className={layoutStyles.infoBannerIcon}
                    fontSize="small"
                    opticalSize={20}
                  />
                  <Typography
                    component="span"
                    className={layoutStyles.infoBannerText}
                  >
                    It is required for the primary facility to be visible. To
                    edit the visibility, you must first set another facility to
                    be primary.
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : null}
          <Controller
            name="facility.visible"
            control={control}
            render={({ value, onChange }) => {
              const isVisible = value !== false

              return (
                <>
                  <SettingsChoiceRow
                    icon={<VisibilityOutlinedIcon />}
                    title="Show facility"
                    description="Choose to display this facility on your coach profile and set as your primary facility (optional)."
                    checked={isVisible}
                    disabled={isPrimary}
                    onSelect={() => onChange(true)}
                    classes={settingsStyles}
                  />
                  {isVisible ? (
                    <Controller
                      name="facility.primary"
                      control={control}
                      render={({
                        onChange: onPrimaryChange,
                        value: primaryValue,
                      }) => (
                        <SettingsCheckboxRow
                          nested
                          icon={<StarBorderOutlinedIcon />}
                          title="Set as primary (optional)"
                          description="Your primary facility is used for location-based searches on pga.com. For optimal search results, your service location should match the primary facility."
                          checked={primaryValue}
                          disabled={isPrimary}
                          onChange={onPrimaryChange}
                          checkboxIcon={checkboxIcon}
                          checkboxCheckedIcon={checkboxCheckedIcon}
                          classes={settingsStyles}
                        />
                      )}
                    />
                  ) : null}
                  <SettingsChoiceRow
                    icon={<VisibilityOffOutlinedIcon />}
                    title="Hide Facility"
                    description="Choose to hide this facility on your coach profile. Any booking types associated with this facility will also be hidden on your booking page."
                    checked={!isVisible}
                    disabled={isPrimary}
                    onSelect={() => {
                      onChange(false)
                      setValue('facility.primary', false, { shouldDirty: true })
                    }}
                    classes={settingsStyles}
                  />
                </>
              )
            }}
          />
        </SettingsSection>
      )}

      <SettingsSection eyebrow="Facility Information" classes={settingsStyles}>
        {preventExternalFacilityEdit && facilityPersisted ? (
          <Box className={layoutStyles.infoBannerWrapper}>
            <Box className={layoutStyles.infoBanner}>
              <Box className={layoutStyles.infoBannerCopy}>
                <InfoOutlinedIcon
                  className={layoutStyles.infoBannerIcon}
                  fontSize="small"
                  opticalSize={20}
                />
                <Typography
                  component="span"
                  className={layoutStyles.infoBannerText}
                >
                  Editing this pre-existing facility is disabled. If you feel
                  this information is incorrect, please email the team to update
                  the information at{' '}
                  <Link
                    href="mailto:membership@pgahq.com"
                    className={classes.link}
                  >
                    membership@pgahq.com
                  </Link>
                  .
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}
        <Box
          className={`${layoutStyles.fieldsStack} ${
            preventExternalFacilityEdit && facilityPersisted
              ? layoutStyles.fieldsStackWithBanner
              : ''
          }`}
        >
          <FacilityInformationFields
            classes={classes}
            control={control}
            register={register}
            errors={errors}
            facility={facility}
            facilityPersisted={facilityPersisted}
            preventExternalFacilityEdit={preventExternalFacilityEdit}
            setFacility={setFacility}
            clearSelectedFacility={clearSelectedFacility}
          />
        </Box>
        <Controller
          name="facility.private"
          control={control}
          render={({ onChange, value }) => (
            <SettingsCheckboxRow
              icon={<LockOutlinedIcon />}
              title="This is a private facility"
              description="Private facilities are marked as members-only on your coach profile."
              checked={value}
              disabled={preventExternalFacilityEdit}
              onChange={onChange}
              checkboxIcon={checkboxIcon}
              checkboxCheckedIcon={checkboxCheckedIcon}
              classes={settingsStyles}
            />
          )}
        />
      </SettingsSection>
    </>
  )
}

SettingsFacilityFormSections.propTypes = {
  facility: PropTypes.object,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  setFacility: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  preventExternalFacilityEdit: PropTypes.bool,
  facilityPersisted: PropTypes.bool,
  clearSelectedFacility: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  hasCoachProfile: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  settingsStyles: PropTypes.object.isRequired,
  layoutStyles: PropTypes.object.isRequired,
}

export default SettingsFacilityFormSections
